// Boost Blenz Admin — date-range hook + picker for Daily Pulse and Ad Performance.
// Fetches /api/metrics for the selected window; the Refresh button re-fetches
// fresh (bypassing the Worker cache). Presets + a custom calendar range.

import React from "react";
import { FIGTREE, useT } from "./theme";
import { Icon } from "./atoms";
import { useCachedResource } from "./datacache";

export const RANGE_OPTS = [
  { id: "today", label: "Today" },
  { id: "yesterday", label: "Yesterday" },
  { id: "wtd", label: "WTD" },
  { id: "mtd", label: "MTD" },
  { id: "lastmonth", label: "Last Mo" },
  { id: "l7", label: "L7" },
  { id: "l14", label: "L14" },
  { id: "l30", label: "L30" },
  { id: "l60", label: "L60" },
  { id: "l90", label: "L90" },
];

export function useRangeMetrics(defaultRange = "today") {
  const [range, setRange] = React.useState(defaultRange);
  const [custom, setCustom] = React.useState<{ start?: string; end?: string }>({});

  // A complete custom range needs both ends; until then we hold the fetch.
  const ready = range !== "custom" || !!(custom.start && custom.end);
  // Cache key is the resolved window, so revisiting a range you already viewed
  // (e.g. Daily Pulse ⇄ Ads on the same window) paints instantly from cache.
  const key = ready
    ? `metrics:${range}${range === "custom" ? `:${custom.start}:${custom.end}` : ""}`
    : null;

  const res = useCachedResource<any>(key, async ({ fresh }) => {
    const p = new URLSearchParams({ range });
    if (range === "custom" && custom.start && custom.end) {
      p.set("start", custom.start);
      p.set("end", custom.end);
    }
    if (fresh) p.set("fresh", "1");
    const r = await fetch(`/api/metrics?${p.toString()}`);
    if (!r.ok) throw new Error(String(r.status));
    return await r.json();
  }, { enabled: ready });

  return {
    range, setRange, custom, setCustom,
    data: res.data,
    // `loading` drives the Refresh icon's spin (the pages render from `data` with
    // fallbacks, so they never blank on it). Keep it true whenever a request is in
    // flight — but because `data` now persists in the cache across navigations, a
    // revisit paints instantly from cache while it quietly revalidates.
    loading: res.loading || res.revalidating,
    refresh: res.refresh,
  };
}

export function DateRangePicker({ range, setRange, custom, setCustom, onRefresh, loading }: any) {
  const t = useT();
  const [showCustom, setShowCustom] = React.useState(range === "custom");
  const pill = (active: boolean): any => ({
    fontFamily: FIGTREE, fontWeight: 700, fontSize: 11, letterSpacing: "0.03em", textTransform: "uppercase",
    padding: "5px 10px", borderRadius: 999, border: "none", cursor: "pointer",
    background: active ? t.fg : "transparent", color: active ? t.surface : t.muted, transition: "background 140ms",
  });
  const dateInput: any = {
    fontFamily: FIGTREE, fontSize: 12, padding: "5px 8px", borderRadius: 8,
    border: "1px solid " + t.borderStrong, background: t.surface, color: t.fg, outline: "none",
  };
  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
      <div style={{ display: "inline-flex", background: t.surface2, border: "1px solid " + t.border, borderRadius: 999, padding: 2, flexWrap: "wrap" }}>
        {RANGE_OPTS.map((o) => (
          <button key={o.id} onClick={() => { setShowCustom(false); setRange(o.id); }} style={pill(range === o.id)}>{o.label}</button>
        ))}
        <button onClick={() => setShowCustom((s) => !s)} style={pill(range === "custom")}>Custom</button>
      </div>
      {showCustom && (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <input type="date" value={custom.start || ""} max={custom.end || undefined} onChange={(e) => setCustom({ ...custom, start: e.target.value })} style={dateInput} />
          <span style={{ color: t.muted, fontFamily: FIGTREE }}>–</span>
          <input type="date" value={custom.end || ""} min={custom.start || undefined} onChange={(e) => setCustom({ ...custom, end: e.target.value })} style={dateInput} />
          <button onClick={() => custom.start && custom.end && setRange("custom")} disabled={!(custom.start && custom.end)}
            style={{ fontFamily: FIGTREE, fontWeight: 700, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.04em", padding: "6px 11px", borderRadius: 999, border: "none", cursor: custom.start && custom.end ? "pointer" : "not-allowed", background: t.accent, color: "#fff", opacity: custom.start && custom.end ? 1 : 0.5 }}>Apply</button>
        </span>
      )}
      <button onClick={onRefresh} title="Refresh now" aria-label="Refresh" style={{
        width: 32, height: 32, borderRadius: 999, border: "1px solid " + t.border, background: t.surface2,
        cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
      }}>
        <Icon name="refresh" size={15} color={t.fg2} style={{ animation: loading ? "bbspin 0.8s linear infinite" : "none" }} />
      </button>
    </div>
  );
}
